import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";
import {
  BookOpen,
  Compass,
  Library,
  Layers,
  ListChecks,
  LayoutDashboard,
  Settings,
  ShieldCheck,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { useState, type ReactNode, type ComponentType } from "react";

export const STATUS_FLOW = [
  { key: "REGISTERED", label: "Registered", description: "Item is logged into the system. No work has begun yet." },
  { key: "IN_PROGRESS", label: "In Progress", description: "Active work is underway on this item." },
  { key: "WITH_SDF_MD_REVIEW", label: "With SDF MD Review", description: "Item is being reviewed by the SDF Managing Director." },
  { key: "WITH_EDGE_MD_REVIEW", label: "With EDGE MD Review", description: "Item is being reviewed by the EDGE Managing Director." },
  { key: "WITH_SECRETARIAT_TO_SEND", label: "With Secretariat to Send", description: "Secretariat is preparing the item for upward submission." },
  { key: "AWAITING_DISPATCH", label: "Awaiting Dispatch", description: "Above-MD item has been approved by SDF MD. PMO/Board Sec must dispatch it upward within the working-day target." },
  { key: "SENT_UPWARD", label: "Sent Upward", description: "Item has been dispatched upward. A dispatch note records where it was sent. Terminal state for the upward path." },
  { key: "AWAITING_RESPONSE", label: "Awaiting Response", description: "Waiting for a response from the recipient authority." },
  { key: "SIGNED_OFF", label: "Signed Off", description: "The required sign-off authority has approved this item." },
  { key: "CLOSED", label: "Closed", description: "Item is complete. A closing note explains the resolution. Terminal state." },
  { key: "CANCELLED", label: "Cancelled", description: "Item was cancelled before completion. Terminal state." },
];

export const STATUS_COLORS: Record<string, string> = {
  REGISTERED: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  IN_PROGRESS: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  WITH_SDF_MD_REVIEW: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  WITH_EDGE_MD_REVIEW: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  WITH_SECRETARIAT_TO_SEND: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  AWAITING_DISPATCH: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  SENT_UPWARD: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  AWAITING_RESPONSE: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  SIGNED_OFF: "bg-green-500/20 text-green-300 border-green-500/30",
  CLOSED: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  CANCELLED: "bg-red-500/20 text-red-300 border-red-500/30",
};

export function StatusBadge({ status }: { status: string }) {
  const found = STATUS_FLOW.find((s) => s.key === status);
  return (
    <Badge variant="outline" className={`text-xs ${STATUS_COLORS[status] || ""}`}>
      {found?.label || status}
    </Badge>
  );
}

export function RoleBadge({ role }: { role: string }) {
  if (role === "super_admin") {
    return <Badge variant="outline" className="text-xs bg-purple-500/15 text-purple-300 border-purple-500/30">Super Admin</Badge>;
  }
  if (role === "it_admin") {
    return <Badge variant="outline" className="text-xs bg-blue-500/15 text-blue-300 border-blue-500/30">IT Admin+</Badge>;
  }
  if (role === "hr_admin") {
    return <Badge variant="outline" className="text-xs bg-teal-500/15 text-teal-300 border-teal-500/30">HR Admin+</Badge>;
  }
  if (role === "admin") {
    return <Badge variant="outline" className="text-xs bg-green-500/15 text-green-300 border-green-500/30">General Admin+</Badge>;
  }
  if (role === "md_admin") {
    return <Badge variant="outline" className="text-xs bg-amber-500/15 text-amber-300 border-amber-500/30">MD Admin</Badge>;
  }
  if (role === "member") {
    return <Badge variant="outline" className="text-xs bg-slate-500/15 text-slate-300 border-slate-500/30">Members</Badge>;
  }
  return <Badge variant="outline" className="text-xs bg-white/5 text-white/40 border-white/10">All Users</Badge>;
}

export function PageHeading({ title, description, icon: Icon }: { title: string; description?: string; icon?: ComponentType<{ className?: string }> }) {
  return (
    <div className="mb-6" data-testid="text-guide-page-heading">
      <div className="flex items-center gap-3 mb-2">
        {Icon && (
          <div className="w-9 h-9 rounded-md bg-cyan-500/15 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-cyan-400" />
          </div>
        )}
        <h1 className="text-2xl font-bold text-white">{title}</h1>
      </div>
      {description && <p className="text-white/50 text-sm leading-relaxed">{description}</p>}
    </div>
  );
}

export function Section({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <Card className="bg-white/[0.03] border-white/10 mb-4">
      <CardContent className="p-5 space-y-4">
        {title && <h2 className="text-base font-semibold text-white">{title}</h2>}
        {children}
      </CardContent>
    </Card>
  );
}

export function Steps({ steps }: { steps: ReactNode[] }) {
  return (
    <div className="bg-white/[0.03] rounded-md p-4 space-y-2.5">
      {steps.map((step, i) => (
        <div key={i} className="flex items-start gap-3 text-xs text-white/65 leading-relaxed">
          <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-300 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
          <div className="flex-1">{step}</div>
        </div>
      ))}
    </div>
  );
}

export function Field({
  name,
  required,
  children,
}: {
  name: string;
  required?: "required" | "optional" | "auto" | "locked";
  children: ReactNode;
}) {
  const reqColor = required === "required"
    ? "bg-red-500/15 text-red-300 border-red-500/30"
    : required === "auto"
    ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/30"
    : required === "locked"
    ? "bg-slate-500/15 text-slate-300 border-slate-500/30"
    : "bg-white/5 text-white/40 border-white/10";
  const reqLabel = required === "required" ? "Required" : required === "auto" ? "Auto-filled" : required === "locked" ? "Locked" : "Optional";
  return (
    <div className="border border-white/5 rounded-md p-3 bg-white/[0.02]" data-testid={`field-${name.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <p className="text-sm text-white font-medium">{name}</p>
        <Badge variant="outline" className={`text-2xs uppercase tracking-wider ${reqColor}`}>{reqLabel}</Badge>
      </div>
      <div className="text-xs text-white/55 leading-relaxed space-y-1.5">{children}</div>
    </div>
  );
}

export function Callout({
  variant = "info",
  title,
  children,
}: {
  variant?: "info" | "warning" | "tip" | "danger";
  title?: string;
  children: ReactNode;
}) {
  const palette = {
    info: { bg: "bg-cyan-500/8", border: "border-cyan-500/25", title: "text-cyan-300" },
    warning: { bg: "bg-amber-500/8", border: "border-amber-500/25", title: "text-amber-300" },
    tip: { bg: "bg-emerald-500/8", border: "border-emerald-500/25", title: "text-emerald-300" },
    danger: { bg: "bg-red-500/8", border: "border-red-500/25", title: "text-red-300" },
  }[variant];
  return (
    <div className={`rounded-md ${palette.bg} border ${palette.border} p-3`}>
      {title && <p className={`text-xs font-semibold ${palette.title} mb-1.5 uppercase tracking-wider`}>{title}</p>}
      <div className="text-xs text-white/65 leading-relaxed space-y-1">{children}</div>
    </div>
  );
}

export function NextStep({ children }: { children: ReactNode }) {
  return (
    <div className="bg-emerald-500/8 border border-emerald-500/25 rounded-md p-3">
      <p className="text-xs font-semibold text-emerald-300 uppercase tracking-wider mb-1.5">After You Submit</p>
      <div className="text-xs text-white/65 leading-relaxed space-y-1">{children}</div>
    </div>
  );
}

export type NavItem = { slug: string; label: string };
export type NavGroup = { id: string; label: string; icon: ComponentType<{ className?: string }>; items: NavItem[] };

export const NAV_TREE: NavGroup[] = [
  {
    id: "start",
    label: "Get Started",
    icon: Compass,
    items: [
      { slug: "", label: "Overview" },
      { slug: "quick-start", label: "Quick Start by Role" },
      { slug: "glossary", label: "Glossary" },
    ],
  },
  {
    id: "concepts",
    label: "Concepts",
    icon: BookOpen,
    items: [
      { slug: "roles", label: "Roles & Access" },
      { slug: "account", label: "Your Account" },
      { slug: "lifecycle", label: "Action Item Lifecycle" },
      { slug: "lanes", label: "Authority Lanes" },
      { slug: "responsibility", label: "Responsibility & Handoffs" },
      { slug: "handoff-deadlines", label: "Handoff Deadlines & Fairness" },
      { slug: "sub-tasks", label: "Sub-Task Assignments" },
      { slug: "sign-off", label: "Sign-off, Review & Closing" },
      { slug: "upward-dispatch", label: "Upward Dispatch" },
      { slug: "soft-delete", label: "Soft Deletion & Restore" },
      { slug: "notifications", label: "Email Notifications" },
    ],
  },
  {
    id: "forms",
    label: "How-To: Filling Forms",
    icon: ListChecks,
    items: [
      { slug: "forms/register", label: "Register an Action Item" },
      { slug: "forms/update", label: "Add a Progress Update" },
      { slug: "forms/handoff", label: "Hand Off Responsibility" },
      { slug: "forms/return", label: "Return for Amendments" },
      { slug: "forms/mark-dispatched", label: "Mark Dispatched" },
      { slug: "forms/approve-close", label: "Approve & Close (MD)" },
      { slug: "forms/sub-task", label: "Create / Complete a Sub-Task" },
      { slug: "forms/meeting-tasks", label: "Log Meeting Tasks" },
      { slug: "forms/send-for-review", label: "Send for Review" },
      { slug: "forms/password", label: "Change / Reset Password" },
    ],
  },
  {
    id: "pages",
    label: "Page-by-Page",
    icon: LayoutDashboard,
    items: [
      { slug: "pages/summary", label: "Summary" },
      { slug: "pages/origins", label: "Origins" },
      { slug: "pages/register", label: "Register" },
      { slug: "pages/updates", label: "Updates" },
      { slug: "pages/meeting-tasks", label: "Meeting Tasks" },
      { slug: "pages/md-meetings", label: "MD Meetings" },
      { slug: "pages/hr-dossier", label: "HR Dossier" },
      { slug: "pages/escalations", label: "Escalations Inbox" },
      { slug: "pages/detail", label: "Action Item Detail" },
    ],
  },
  {
    id: "admin",
    label: "Admin & Configuration",
    icon: Settings,
    items: [
      { slug: "admin-tabs", label: "Admin Tabs — Overview" },
      { slug: "admin/kpi", label: "  · KPI Dashboard" },
      { slug: "admin/team-members", label: "  · Team Members" },
      { slug: "admin/user-accounts", label: "  · User Accounts" },
      { slug: "admin/departments", label: "  · Departments & Sections" },
      { slug: "admin/versions", label: "  · Versions (Governance)" },
      { slug: "admin/lanes", label: "  · Authority Lanes" },
      { slug: "admin/actors", label: "  · Authority Actors" },
      { slug: "admin/reference-lists", label: "  · Reference Lists" },
      { slug: "admin/workflow-rules", label: "  · Workflow Rules" },
      { slug: "admin/field-permissions", label: "  · Field Permissions" },
      { slug: "admin/transition-permissions", label: "  · Transition Permissions" },
      { slug: "admin/roles-permissions", label: "  · Roles & Permissions" },
      { slug: "admin/reminder-schedule", label: "  · Reminder Schedule" },
      { slug: "admin/deleted-items", label: "  · Deleted Items" },
      { slug: "settings-tabs", label: "Settings Tabs — Overview" },
      { slug: "settings/email", label: "  · Email Settings (SMTP)" },
      { slug: "settings/alert-dispatcher", label: "  · IT Alert Dispatcher" },
      { slug: "settings/branding", label: "  · Branding" },
      { slug: "settings/backup", label: "  · Backup & Restore" },
      { slug: "settings/api-keys", label: "  · Agent API Keys" },
      { slug: "settings/live-refresh", label: "  · Live Refresh & Performance" },
      { slug: "settings/system-log", label: "  · System Diagnostics Log" },
      { slug: "agent-api", label: "Agent API (Jeeves) — Integrator Reference" },
    ],
  },
  {
    id: "reference",
    label: "Reference",
    icon: Library,
    items: [
      { slug: "status-reference", label: "Status Reference Table" },
      { slug: "event-reference", label: "Audit Event Reference" },
      { slug: "whats-new", label: "What's New (2.6 → 2.10)" },
    ],
  },
];

export function pathFor(slug: string): string {
  return slug ? `/guide/${slug}` : "/guide";
}

export function GuideSidebar({ activeSlug }: { activeSlug: string }) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const out: Record<string, boolean> = {};
    NAV_TREE.forEach((g) => {
      out[g.id] = g.items.some((it) => it.slug === activeSlug);
      if (g.id === "start" && activeSlug === "") out[g.id] = true;
    });
    if (Object.values(out).every((v) => !v)) out["start"] = true;
    return out;
  });

  const toggle = (id: string) => setOpenGroups((s) => ({ ...s, [id]: !s[id] }));

  return (
    <nav className="flex flex-col gap-1" data-testid="guide-sidebar">
      {NAV_TREE.map((group) => {
        const isOpen = openGroups[group.id] ?? false;
        const Icon = group.icon;
        return (
          <div key={group.id}>
            <button
              type="button"
              onClick={() => toggle(group.id)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs uppercase tracking-wider text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors"
              data-testid={`button-guide-group-${group.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="flex-1 text-left font-semibold">{group.label}</span>
              {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>
            {isOpen && (
              <div className="flex flex-col gap-0.5 pl-4 mt-0.5 mb-2">
                {group.items.map((item) => {
                  const active = item.slug === activeSlug;
                  return (
                    <Link
                      key={item.slug}
                      href={pathFor(item.slug)}
                      className={`px-2 py-1.5 rounded-md text-xs transition-colors ${
                        active
                          ? "bg-cyan-500/15 text-cyan-300 font-medium"
                          : "text-white/55 hover:text-white hover:bg-white/5"
                      }`}
                      data-testid={`link-guide-${item.slug || "overview"}`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export function PrevNext({ slug }: { slug: string }) {
  const flat: NavItem[] = NAV_TREE.flatMap((g) => g.items);
  const idx = flat.findIndex((i) => i.slug === slug);
  const prev = idx > 0 ? flat[idx - 1] : null;
  const next = idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null;
  return (
    <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-white/10" data-testid="guide-prev-next">
      {prev ? (
        <Link href={pathFor(prev.slug)} className="flex items-center gap-2 text-xs text-white/50 hover:text-white transition-colors" data-testid="link-guide-prev">
          <ChevronRight className="w-3 h-3 rotate-180" />
          <span className="text-white/30">Previous:</span>
          <span>{prev.label}</span>
        </Link>
      ) : <span />}
      {next ? (
        <Link href={pathFor(next.slug)} className="flex items-center gap-2 text-xs text-white/50 hover:text-white transition-colors ml-auto" data-testid="link-guide-next">
          <span className="text-white/30">Next:</span>
          <span>{next.label}</span>
          <ChevronRight className="w-3 h-3" />
        </Link>
      ) : <span />}
    </div>
  );
}

export const ICONS = {
  ShieldCheck,
  Layers,
};

/* ─────────────────────────────────────────────────────────────────────────
   Labelled-diagram toolkit
   Mock "screen" pictures with numbered call-outs, used across the guide so a
   non-technical reader can see exactly where to click and what to fill in.
   The Marker numbers in a diagram match the numbers in the Steps below it.
   ───────────────────────────────────────────────────────────────────────── */

// A small numbered circle. Use the same number in the diagram and in the steps.
export function Marker({ n }: { n: number }) {
  return (
    <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center shrink-0 ring-1 ring-cyan-400/50 align-middle">
      {n}
    </span>
  );
}

// A faux app-window frame so a diagram reads as "a picture of a screen".
export function DiagramFrame({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#0b1322] overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-white/[0.02]">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400/40" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/40" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/40" />
        {label && <span className="ml-2 text-2xs text-white/45 font-mono truncate">{label}</span>}
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

export type MockTab = { label: string; active?: boolean; marker?: number };

export function MockTabs({ tabs }: { tabs: MockTab[] }) {
  return (
    <div className="flex flex-wrap gap-1 border-b border-white/10 pb-px">
      {tabs.map((t, i) => (
        <span
          key={i}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-t-md ${
            t.active
              ? "text-cyan-200 bg-cyan-500/10 border-b-2 border-cyan-400 font-medium"
              : "text-white/45"
          }`}
        >
          {t.marker && <Marker n={t.marker} />}
          {t.label}
        </span>
      ))}
    </div>
  );
}

// A faux labelled input box.
export function MockField({
  label,
  value,
  placeholder,
  marker,
}: {
  label?: string;
  value?: string;
  placeholder?: string;
  marker?: number;
}) {
  return (
    <div>
      {label && (
        <div className="flex items-center gap-1.5 mb-1">
          {marker && <Marker n={marker} />}
          <span className="text-2xs uppercase tracking-wide text-white/45">{label}</span>
        </div>
      )}
      <div className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-xs">
        {value ? <span className="text-white/80">{value}</span> : <span className="text-white/30">{placeholder || "…"}</span>}
      </div>
    </div>
  );
}

// A faux dropdown. Pass open + options to show it expanded.
export function MockSelect({
  label,
  value,
  marker,
  open,
  options,
}: {
  label?: string;
  value?: string;
  marker?: number;
  open?: boolean;
  options?: string[];
}) {
  return (
    <div className="relative">
      {label && (
        <div className="flex items-center gap-1.5 mb-1">
          {marker && <Marker n={marker} />}
          <span className="text-2xs uppercase tracking-wide text-white/45">{label}</span>
        </div>
      )}
      <div className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-xs">
        {value ? <span className="text-white/80">{value}</span> : <span className="text-white/30">Select…</span>}
        <ChevronDown className="w-3.5 h-3.5 text-white/40" />
      </div>
      {open && options && options.length > 0 && (
        <div className="mt-1 rounded-md border border-cyan-400/30 bg-[#0e1828] py-1 shadow-lg">
          {options.map((o, i) => (
            <div key={i} className="px-3 py-1.5 text-xs text-white/70 hover:bg-cyan-500/10">{o}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export function MockButton({
  label,
  marker,
  variant = "primary",
}: {
  label: string;
  marker?: number;
  variant?: "primary" | "ghost" | "danger";
}) {
  const cls =
    variant === "primary"
      ? "bg-cyan-600/80 text-white"
      : variant === "danger"
      ? "bg-red-600/70 text-white"
      : "border border-white/15 text-white/70";
  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      {marker && <Marker n={marker} />}
      <span className={`inline-block rounded-md text-xs font-medium px-3 py-1.5 ${cls}`}>{label}</span>
    </span>
  );
}

// A faux KPI / metric tile.
export function MockKpi({
  label,
  value,
  marker,
  tone = "default",
}: {
  label: string;
  value: string;
  marker?: number;
  tone?: "default" | "good" | "warn" | "bad";
}) {
  const toneCls =
    tone === "good"
      ? "text-emerald-300"
      : tone === "warn"
      ? "text-amber-300"
      : tone === "bad"
      ? "text-red-300"
      : "text-white";
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 min-w-[96px]">
      <div className="flex items-center gap-1.5">
        {marker && <Marker n={marker} />}
        <span className="text-2xs uppercase tracking-wide text-white/45">{label}</span>
      </div>
      <p className={`text-lg font-bold mt-0.5 ${toneCls}`}>{value}</p>
    </div>
  );
}

// A faux table.
export function MockTable({
  columns,
  rows,
}: {
  columns: ReactNode[];
  rows: ReactNode[][];
}) {
  return (
    <div className="rounded-md border border-white/10 overflow-hidden">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-white/[0.04] text-white/50">
            {columns.map((c, i) => (
              <th key={i} className="px-3 py-1.5 text-left font-medium">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-t border-white/5 text-white/70">
              {r.map((cell, ci) => (
                <td key={ci} className="px-3 py-1.5">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// A simple horizontal row wrapper for laying mock elements side by side.
export function MockRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-end gap-2">{children}</div>;
}

// The high-level component a section uses: a titled frame + a numbered legend.
// The legend numbers correspond to the <Marker n=…/> badges inside `children`.
export function Diagram({
  title,
  frameLabel,
  children,
  legend,
}: {
  title?: string;
  frameLabel?: string;
  children: ReactNode;
  legend?: ReactNode[];
}) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-3 mb-2" data-testid="guide-diagram">
      {title && <p className="text-xs font-semibold text-white/80">{title}</p>}
      <DiagramFrame label={frameLabel}>{children}</DiagramFrame>
      {legend && legend.length > 0 && (
        <div className="rounded-md bg-white/[0.03] p-3">
          <p className="text-2xs uppercase tracking-wider text-cyan-300/80 mb-2 font-semibold">Follow the numbers</p>
          <ol className="space-y-1.5">
            {legend.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-white/70 leading-relaxed">
                <Marker n={i + 1} />
                <span className="flex-1">{item}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
